import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Save, Building2 } from 'lucide-react';
import partnerAdminService from '../../services/partnerAdminService';
import PermissionCheckboxGroup from '../PlatformAdmin/PermissionCheckboxGroup';
import { PERMISSIONS } from '../../constants/permissions';
import { LIMITS_COPY, PARTNER_ADMIN_COPY } from '../../constants/partnerCopy';
import { apiErrorText, describeApiError } from '../../utils/apiError';
import { platformMcpTooltips, platformMcpUntickWarning } from '../../utils/platformMcpPermission';
import styles from '../PlatformAdmin/PlatformAdmin.module.scss';

const RUN_CONTENT = PERMISSIONS.USAGE_RUN_CONTENT;

/**
 * A client company's feature limits (Partner tier guide §5.3), chosen within the
 * partner's plan. Three kinds of key can show up:
 *   - plan keys (minus usage_run_content) — the editable options;
 *   - usage_run_content in the company's list — enabled by the platform: shown
 *     checked and locked, never sent (the backend keeps it regardless);
 *   - keys no longer in the plan (the platform narrowed the partner) — already
 *     inactive: shown unchecked and disabled, never sent (sending them is a 400).
 * The PUT carries only the ticked options.
 */
const PartnerEntitlementsPanel = ({ ctx, notify, intent }) => {
  const { companies, partnerName, reloadCompanies } = ctx;
  const [companyId, setCompanyId] = useState(intent?.companyId ?? '');
  const [plan, setPlan] = useState(null); // string[] | null
  const [companyKeys, setCompanyKeys] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // A jump from "Review feature limits" picks the new company.
  useEffect(() => {
    if (intent?.companyId != null) setCompanyId(intent.companyId);
  }, [intent]);

  // Default to the first company once the list arrives.
  useEffect(() => {
    if (companyId === '' && companies.length) setCompanyId(companies[0].id);
  }, [companies, companyId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await partnerAdminService.getPlan();
        if (!cancelled) setPlan(Array.isArray(data?.permission_keys) ? data.permission_keys : []);
      } catch (err) {
        if (!cancelled) notify(describeApiError(err, 'Failed to load your plan.'), 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notify]);

  const options = useMemo(() => (plan || []).filter((k) => k !== RUN_CONTENT), [plan]);

  const loadCompany = useCallback(async () => {
    if (companyId === '' || companyId == null) return;
    try {
      setLoading(true);
      const data = await partnerAdminService.getCompanyEntitlements(companyId);
      const keys = Array.isArray(data?.permission_keys) ? data.permission_keys : [];
      setCompanyKeys(keys);
    } catch (err) {
      if (err?.status === 404) {
        notify(PARTNER_ADMIN_COPY.noLongerInPartner(partnerName), 'warning');
        setCompanyId('');
        reloadCompanies();
      } else {
        notify(describeApiError(err, "Failed to load the company's limits."), 'error');
      }
    } finally {
      setLoading(false);
    }
  }, [companyId, notify, partnerName, reloadCompanies]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  // Ticked = company keys that are still valid options.
  useEffect(() => {
    const allowed = new Set(options);
    setSelected(companyKeys.filter((k) => allowed.has(k)));
  }, [companyKeys, options]);

  const runContentOn = companyKeys.includes(RUN_CONTENT);
  const staleKeys = useMemo(() => {
    const allowed = new Set(options);
    return companyKeys.filter((k) => k !== RUN_CONTENT && !allowed.has(k));
  }, [companyKeys, options]);

  const catalog = useMemo(() => {
    const keys = [...options, ...staleKeys, ...(runContentOn ? [RUN_CONTENT] : [])];
    return keys.map((key) => ctx.permissionEntry(key));
  }, [options, staleKeys, runContentOn, ctx]);

  const keyTooltips = useMemo(() => {
    const map = { ...platformMcpTooltips('companyLimits') };
    staleKeys.forEach((k) => { map[k] = LIMITS_COPY.noLongerInPlan; });
    if (runContentOn) map[RUN_CONTENT] = LIMITS_COPY.enabledByPlatform;
    return map;
  }, [staleKeys, runContentOn]);

  const lockedKeys = useMemo(() => (runContentOn ? [RUN_CONTENT] : []), [runContentOn]);

  const toggle = (key) =>
    setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const company = companies.find((c) => c.id === companyId) || null;
  const companyName = company?.name || 'this company';

  const save = async () => {
    try {
      setSaving(true);
      const allowed = new Set(options);
      await partnerAdminService.setCompanyEntitlements(
        companyId,
        selected.filter((k) => allowed.has(k))
      );
      notify(LIMITS_COPY.partnerSaved(companyName), 'success');
      // Declarative + last write wins: show what the server now holds.
      await loadCompany();
    } catch (err) {
      const m = apiErrorText(err).match(/Permission keys not allowed here:\s*(.+)$/i);
      if (err?.status === 400 && m) {
        notify(LIMITS_COPY.keysNotAllowed(m[1].trim()), 'error');
      } else {
        notify(describeApiError(err, 'Failed to save feature limits.'), 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  if (companies.length === 0) {
    return (
      <Box className={styles.emptyState}>
        <Building2 className={styles.emptyIcon} size={56} />
        <Typography variant="h6">No companies yet</Typography>
        <Typography variant="body2">Create a company first, then choose which features it can use.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box className={styles.toolbar}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Company</InputLabel>
          <Select label="Company" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            {companies.map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {LIMITS_COPY.partnerEditorHelp(companyName)}
      </Typography>

      {loading || plan === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : options.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Your plan doesn't include any features yet. Contact the platform.
        </Typography>
      ) : (
        <>
          <PermissionCheckboxGroup
            catalog={catalog}
            selected={selected}
            onToggle={toggle}
            disabledKeys={staleKeys}
            lockedKeys={lockedKeys}
            keyTooltips={keyTooltips}
            keyWarnings={platformMcpUntickWarning(companyKeys, selected)}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {selected.length} of {options.length} features on
            </Typography>
            <Button
              variant="contained"
              startIcon={<Save size={16} />}
              className={styles.primaryBtn}
              onClick={save}
              disabled={saving || companyId === ''}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Save limits'}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default PartnerEntitlementsPanel;
